// <!-- GRACE:MODULE_START name="ui_auth" -->
// <!-- GRACE:DEPENDENCY target="ui_config" -->
// <LLM_NOTE>
// Токен хранится в localStorage под ключом 'auth_token'.
// localStorage уязвим к XSS-атакам — токен доступен любому JS на странице.
// В production рассмотреть httpOnly cookie как альтернативу.
// </LLM_NOTE>
// Authentication service for frontend API calls
import API_ENDPOINTS from '../config/api';

const TOKEN_KEY = 'auth_token';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

// ── Token management ──────────────────────────────────────────────────

// <!-- GRACE:CONTRACT function="getToken" -->
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// <!-- GRACE:CONTRACT function="setToken" -->
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

// <!-- GRACE:CONTRACT function="removeToken" -->
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// <!-- GRACE:CONTRACT function="authHeaders" -->
/** Return headers with Authorization bearer token (if available) */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ── Helpers ───────────────────────────────────────────────────────────

// <!-- GRACE:CONTRACT function="parseJsonResponse" -->
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (e) {
      const text = await response.text();
      throw new Error(`Ошибка сервера: ${text.substring(0, 200)}`);
    }
  }
  const text = await response.text();
  throw new Error(`Ошибка сервера: ${text.substring(0, 200)}`);
}

// ── API calls ─────────────────────────────────────────────────────────

// <!-- GRACE:CONTRACT function="register" -->
export async function register(
  email: string,
  password: string,
  fullName?: string
): Promise<{ message: string }> {
  const response = await fetch(API_ENDPOINTS.AUTH.REGISTER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, full_name: fullName || null }),
  });

  const data = await parseJsonResponse<any>(response);

  if (!response.ok) {
    throw new Error(data.detail || 'Ошибка регистрации');
  }
  return data;
}

// <!-- GRACE:CONTRACT function="login" -->
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await parseJsonResponse<any>(response);

  if (!response.ok) {
    throw new Error(data.detail || 'Ошибка авторизации');
  }

  setToken(data.access_token);
  return data;
}

// <!-- GRACE:CONTRACT function="getCurrentUser" -->
export async function getCurrentUser(): Promise<AuthUser> {
  const response = await fetch(API_ENDPOINTS.AUTH.ME, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    removeToken();
    throw new Error('Не авторизован');
  }
  return response.json();
}

// <!-- GRACE:CONTRACT function="changePassword" -->
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await fetch(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });

  const data = await parseJsonResponse<any>(response);

  if (!response.ok) {
    throw new Error(data.detail || 'Ошибка смены пароля');
  }
  return data;
}

// <!-- GRACE:CONTRACT function="requestPasswordReset" -->
export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const response = await fetch(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await parseJsonResponse<any>(response);

  if (!response.ok) {
    throw new Error(data.detail || 'Ошибка отправки письма');
  }
  return data;
}

// <!-- GRACE:CONTRACT function="resetPassword" -->
export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const response = await fetch(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });

  const data = await parseJsonResponse<any>(response);

  if (!response.ok) {
    throw new Error(data.detail || 'Ошибка смены пароля');
  }
  return data;
}

// <!-- GRACE:CONTRACT function="logout" -->
export function logout(): void {
  removeToken();
}

// <!-- GRACE:MODULE_END name="ui_auth" -->
